// 209 §5 [RULED 18 — Laurent, 2026-09-25] — THE CASE'S LANGUAGE, IN THE BROWSER.
//
// « Text addressed to a client is in that client's language; a client without
// one reads its case's language; every other text is in the case's language.
// No language is ever assumed. » Every case and collective case carries its own
// (`language`, chosen when it is created — the case form requires it). The rule
// itself is src/lib/docLang.mjs, both ends; this module holds what only the
// screen needs:
//
//   · THE CHOICES: the languages the dictionary carries (the i18n provider's
//     `available`), each named by the PLATFORM in the reader's language
//     (`Intl.DisplayNames`) — never a list written per language: German comes
//     next, and needs dictionary rows only (ruling 16's way, for plurals);
//   · THE REFUSAL: a text that needs the case's language and finds none is
//     refused by the server (`case.language_required`, naming the matter), and
//     the screen offers the case form one click away — a toast action that asks
//     the app to open it (`OPEN_MATTER_FORM`, the `lx:reload-time-entries`
//     way: whoever holds the form listens).
import type { ToastAction } from "../components/Toast";

export const OPEN_MATTER_FORM = "lx:open-matter-form";

/** The matter a refusal names: a case or a collective case. */
export interface MatterRef {
  caseId?: number;
  ccId?: number;
}

/**
 * The matter a `case.language_required` refusal names, or null when the body
 * is any other answer. Takes `unknown`: it is the seam where an error body is
 * read.
 */
export function languageRefusalMatter(body: unknown): MatterRef | null {
  if (typeof body !== "object" || body === null) return null;
  const b = body as { code?: unknown; caseId?: unknown; ccId?: unknown };
  if (b.code !== "case.language_required") return null;
  const caseId = Number(b.caseId);
  const ccId = Number(b.ccId);
  if (Number.isInteger(caseId) && caseId > 0) return { caseId };
  if (Number.isInteger(ccId) && ccId > 0) return { ccId };
  return null;
}

/** Ask whoever holds the matter's form to open it. */
export function openMatterForm(m: MatterRef): void {
  globalThis.dispatchEvent(new CustomEvent(OPEN_MATTER_FORM, { detail: m }));
}

/* A collective case's form lives in its own view, which is mounted only on that
   view: a refusal met elsewhere (the Invoices list) navigates there first, and
   the request waits here until the view takes it on arriving. */
let pendingForm: MatterRef | null = null;
/** Keep a request for a view that is not mounted yet. */
export function setPendingMatterForm(m: MatterRef | null): void {
  pendingForm = m;
}
/** The view of collective case `ccId` takes its waiting request: true once, then cleared. */
export function takePendingMatterForm(ccId: number): boolean {
  if (pendingForm?.ccId !== ccId) return false;
  pendingForm = null;
  return true;
}

/** The toast action a refusal carries — the case form, one click away — or null. */
export function languageRefusalAction(body: unknown, label: string): ToastAction | null {
  const m = languageRefusalMatter(body);
  return m ? { label, onClick: () => { openMatterForm(m); } } : null;
}

/* 210 §10 — `languageName` and `importedCaseLanguage` MOVED, verbatim, to
   src/lib/docLang.mjs — the language rule both ends read — so the server's
   firm workbook reads a language cell as §19's imports do. Re-exported here:
   this file's importers are untouched. */
import { languageName, importedCaseLanguage } from "./docLang.mjs";
export { languageName, importedCaseLanguage };

/** The case form's choices: the dictionary's languages, named in the reader's. */
export function languageOptions(available: readonly string[], readerLang: string): (readonly [string, string])[] {
  return available.map((code) => [code, languageName(code, readerLang)] as const);
}

